import React, {useState} from 'react'
import {useIntl} from 'react-intl'
import {useDispatch, useSelector} from 'react-redux'
import styled from '@emotion/styled'
import {MobileOverlayLayout} from 'components/Layouts/MobileOverlayLayout'
import {getLanguageCode} from 'modules/meta/selectors'

import {Button} from '@vio/atlas/Button'
import {useToastController} from '@vio/atlas/context/toastController'
import {Box} from '@vio/atlas/helpers/Box'
import {InputTextarea} from '@vio/atlas/InputTextarea'
import {Overlay} from '@vio/atlas/Overlay'
import {Text} from '@vio/atlas/Text'
import {cssTheme} from '@vio/atlas/themes'
import {useBrand} from '@vio/core/src/_web/brand/hooks/useBrand'
import {trackEvent} from '@vio/core/src/analytics/modules/actions'
import {
  Action,
  AnalyticsContext,
  Category,
  Entity
} from '@vio/core/src/analytics/types/Events'
import {getLocalizedHelpUrl} from '@vio/core/src/brand/utils'
import FormattedMessageWrapper from '@vio/core/src/localization/components/FormattedMessage'

import {StarRating} from './components/StarRating'

const VALUE_TO_TRIGGER_STORE_REVIEW = 3

const OverlayContent = styled.div`
  padding: ${cssTheme.layout.spacing.s500};
  padding-top: ${cssTheme.layout.spacing.s600};
  display: flex;
  flex-direction: column;
  justify-content: space-between;
  height: 100%;
`

const Link = styled('a')`
  font-size: inherit;
  text-decoration: underline;
`

interface ShareYourFeedbackOverlayPropTypes {
  isOpen: boolean
  onClose: () => void
}

export const ShareYourFeedbackOverlay = ({
  isOpen,
  onClose
}: ShareYourFeedbackOverlayPropTypes) => {
  const {brand} = useBrand()
  const [shouldShowSubmit, setShouldShowSubmit] = useState(false)
  const [hasStoreReviewTriggered, setHasStoreReviewTriggered] = useState(false)
  const [value, setValue] = useState<number>(0)
  const [text, setText] = useState<string>('')
  const dispatch = useDispatch()
  const thanksForFeedbackMessage = useToastController()
  const intl = useIntl()
  const languageCode = useSelector(getLanguageCode)

  const onRateChange = value => {
    setValue(value)

    if (value === 0) return setShouldShowSubmit(false)

    setShouldShowSubmit(true)

    if (value >= VALUE_TO_TRIGGER_STORE_REVIEW && !hasStoreReviewTriggered) {
      dispatch(
        trackEvent({
          category: Category.User,
          entity: Entity.AppReview,
          action: Action.Submitted,
          component: 'ShareYourFeedbackOverlayStars',
          analyticsContext: {
            [AnalyticsContext.NpsContext]: {
              npsScore: value
            }
          }
        })
      )

      setHasStoreReviewTriggered(true)
    }
  }

  const onSubmitFeedback = () => {
    dispatch(
      trackEvent({
        category: Category.User,
        entity: Entity.AppReview,
        action: Action.Submitted,
        component: 'ShareYourFeedbackOverlayForm',
        analyticsContext: {
          [AnalyticsContext.NpsContext]: {
            npsScore: value,
            openQuestion: text
          }
        }
      })
    )

    onClose()

    thanksForFeedbackMessage.open(
      <FormattedMessageWrapper
        id="shareYourFeedback.thankYouMessage"
        defaultMessage="Thanks for your feedback!"
      />
    )
  }

  return (
    <Overlay isOpen={isOpen}>
      <MobileOverlayLayout
        headerContent={
          <FormattedMessageWrapper
            id="shareYourFeedback"
            defaultMessage="Share your feedback"
          />
        }
        onBackButtonClick={onClose}
      >
        <OverlayContent>
          <div>
            <Text variant="titleL">
              <FormattedMessageWrapper
                id="shareYourFeedback.subtitle"
                defaultMessage="Tell us what we can do better"
              />
            </Text>

            <Box paddingTop="s300">
              <Text variant="bodyS">
                <FormattedMessageWrapper
                  id="shareYourFeedback.text"
                  defaultMessage="We’re not able to respond to queries about specific bookings via this form. For booking assistance or any other questions, go to our <HelpCenter></HelpCenter>"
                  values={{
                    HelpCenter: () => (
                      <Link
                        href={getLocalizedHelpUrl(brand, languageCode)}
                        target="_blank"
                        rel="noreferrer"
                      >
                        <FormattedMessageWrapper
                          id="shareYourFeedback.helpCenter"
                          defaultMessage="help center"
                        />
                      </Link>
                    )
                  }}
                />
              </Text>
            </Box>

            <Box paddingTop="s700" paddingBottom="s400">
              <Text variant="bodyS">
                <FormattedMessageWrapper
                  id="shareYourFeedback.howYouRate"
                  defaultMessage="How would you rate your experience?"
                />
              </Text>
            </Box>

            <StarRating totalStars={5} onRateChange={onRateChange} />

            <Box paddingTop="s700" paddingBottom="s200">
              <Text variant="bodyS">
                <FormattedMessageWrapper
                  id="shareYourFeedback.experience"
                  defaultMessage="How could we make your experience better?"
                />
              </Text>
            </Box>
            <InputTextarea
              id="shareYourFeedback.text"
              name="shareYourFeedback.text"
              label={intl.formatMessage({
                id: 'shareYourFeedback.placeholder',
                defaultMessage: 'I wish I could save my details'
              })}
              onChange={text => setText(text)}
              inputHeight={124}
            />
          </div>
          {shouldShowSubmit && (
            <Button fullWidth onClick={onSubmitFeedback}>
              <FormattedMessageWrapper
                id="shareYourFeedback.cta"
                defaultMessage="Submit"
              />
            </Button>
          )}
        </OverlayContent>
      </MobileOverlayLayout>
    </Overlay>
  )
}
